import type { TextStyle, TextStyles } from "./text-styles";

// The runtime half of text styles (see text-styles.ts): the built-in styles and how an element's
// style resolves, with no schema library, so a creator site's script stays small.

/**
 * Site-wide text styles: set the typography of Heading 1 or an "Eyebrow" once, and every element
 * using it follows. An element can still override any field; resetting it returns to the style.
 * Styles hold only what differs from the theme, so an empty style keeps the theme's own look.
 */
export const builtInTextStyles = [
  { id: "heading-1", name: "Heading 1", tag: "h1" },
  { id: "heading-2", name: "Heading 2", tag: "h2" },
  { id: "heading-3", name: "Heading 3", tag: "h3" },
  { id: "body", name: "Body", tag: "p" },
  { id: "small", name: "Small", tag: "p" },
] as const;

/** The typography a style sets: the same fields an element can override. */
export const textStyleFields = [
  "font",
  "fontSize",
  "mobileFontSize",
  "fontWeight",
  "lineHeight",
  "letterSpacing",
  "uppercase",
  "color",
] as const;
export type TextStyleField = (typeof textStyleFields)[number];

export function defaultTextStyle(tag: "h1" | "h2" | "h3" | "p") {
  return tag === "h1"
    ? "heading-1"
    : tag === "h2"
      ? "heading-2"
      : tag === "h3"
        ? "heading-3"
        : "body";
}

/** The style a text element uses: its chosen one, or the one matching its heading level. */
export function textStyleOf(node: {
  tag: "h1" | "h2" | "h3" | "p";
  style?: { textStyle?: string };
}) {
  return node.style?.textStyle ?? defaultTextStyle(node.tag);
}

/** Built-ins first, then the creator's own, in the order they were made. */
export function textStyleChoices(styles: TextStyles | undefined) {
  return [
    ...builtInTextStyles.map((style) => ({ id: style.id, name: style.name, builtIn: true })),
    ...Object.entries(styles ?? {})
      .filter(([id]) => id.startsWith("custom-"))
      .map(([id, style]) => ({ id, name: style.name ?? "Untitled style", builtIn: false })),
  ];
}

/** The fields an element sets itself, on top of its text style. */
export function textStyleOverrides(style: Partial<Record<TextStyleField, unknown>> | undefined) {
  return textStyleFields.filter((field) => style?.[field] !== undefined);
}

/** An element's effective style: its text style's typography, then its own fields. */
export function withTextStyle<S extends { textStyle?: string }>(
  style: S | undefined,
  preset: TextStyle | undefined,
): S | undefined {
  if (!preset) return style;
  const typography = Object.fromEntries(
    textStyleFields.flatMap((field) =>
      preset[field] === undefined ? [] : [[field, preset[field]]],
    ),
  );
  return { ...typography, ...style } as S;
}

export function newTextStyleId() {
  return `custom-${crypto.randomUUID().replace(/-/g, "").slice(0, 10)}`;
}
